import { patchState, signalStore, withMethods, withState } from "@ngrx/signals";
import { ConfirmWindow, WindowOs, WindowType } from "../models/window-model";

type OsState = {
    windows: WindowOs[];
    isloading: boolean;
    confirmWindow: ConfirmWindow | null;
}

const initialOsState: OsState = {
    windows: [],
    isloading: true,
    confirmWindow: null,
};


export const OsStore = signalStore(
    { providedIn: 'root' },
    withState(initialOsState),
    withMethods((store) => {
        // 1. On définit les méthodes dans un objet interne
        const methods = {
            _getMaxZ: (windows: WindowOs[]): number => {
                return windows.length > 0 ? Math.max(...windows.map(w => w.zIndex)) : 10;
            },

            closeWindow: (id: string) => {
                const windows = store.windows();
                patchState(store, {
                    windows: windows.filter(w => w.id !== id)
                });
            },

            toggleFullscreen: (id: string) => {
                const windows = store.windows();
                const target = windows.find(w => w.id === id);
                if (!target) return;

                const isCurrentlyFullscreen = !!target.fullscreen;

                if (isCurrentlyFullscreen) {
                    const restored = windows.map(w => w.id === id ? {
                        ...w,
                        fullscreen: false,
                        x: w._prevX ?? w.x,
                        y: w._prevY ?? w.y,
                        width: w._prevWidth ?? w.width,
                        height: w._prevHeight ?? w.height,
                    } : w);
                    patchState(store, { windows: restored });
                    return;
                }

                const taskbarHeight = 48; // Ou calcul dynamique
                const updated = windows.map(w => {
                    if (w.id !== id) return w;
                    return {
                        ...w,
                        _prevX: w.x,
                        _prevY: w.y,
                        _prevWidth: w.width,
                        _prevHeight: w.height,
                        x: 0,
                        y: 0,
                        width: typeof window !== 'undefined' ? window.innerWidth : 1024,
                        height: (typeof window !== 'undefined' ? window.innerHeight : 768) - taskbarHeight,
                        fullscreen: true,
                        zIndex: methods._getMaxZ(windows) + 1, // On utilise methods.
                    };
                });

                patchState(store, { windows: updated });
            },

            openWindow: (window: Partial<WindowOs> & { type: WindowType }) => {
                const windows = store.windows();
                const id = crypto.randomUUID();

                const newWindow: WindowOs = {
                    id,
                    type: window.type,
                    x: window.x ?? 100 + windows.length * 20,
                    y: window.y ?? 100 + windows.length * 20,
                    width: window.width ?? 600,
                    height: window.height ?? 400,
                    zIndex: methods._getMaxZ(windows) + 1,
                    isAdmin: false,
                    fullscreen: false,
                    itemId: window.itemId,
                    userId: window.userId,
                    data: window.data
                };

                patchState(store, {
                    windows: [...windows, newWindow]
                });

                // 2. On appelle toggleFullscreen via l'objet methods
                if (window.fullscreen) {
                    methods.toggleFullscreen(id);
                }
            },

            updateWindow(id: string, changes: Partial<WindowOs>) {
                const windows = store.windows().map(w => w.id === id ? { ...w, ...changes } : w);
                patchState(store, { windows });
            },

            bringToFront(id: string) {
                const windows = store.windows();
                const maxZ = this._getMaxZ(windows);
                const updatedWindows = windows.map(w => w.id === id ? { ...w, zIndex: maxZ + 1 } : w);
                patchState(store, { windows: updatedWindows });
            },

            openConfirmWindow(config: ConfirmWindow) {
                patchState(store, {
                    confirmWindow: config
                });
            },

            closeConfirmWindow() {
                patchState(store, {
                    confirmWindow: undefined
                });
            }
        };

        // 3. On retourne l'objet contenant toutes les méthodes
        return methods;
    })
);